import weatherDashboardSliceReducer from '@/store/slices/WeatherDashboard/weatherDashboardSlice'
import soilMoistureDashboardSliceReducer from '@/store/slices/SoilMoistureDashboard/soilMoistureDashboardSlice'
import srcInterfaceSliceReducer from '@/store/slices/srcInterfaceSlice'
import { configureStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import storage from 'redux-persist/lib/storage'
import counterSlice from './slices/counterSlice'
import layoutSlice from './slices/layoutSlice'
// import sb3AlertNotification from './slices/sb3AlertNotification'
// import sb3ClimateControlSlice from './slices/sb3ClimateControlSlice'
// import sb3DataComparisonSlice from './slices/sb3DataComparisonSlice'
import dataComparisonSliceReducer from '@/store/slices/dataComparisonSlice'
import dataComparisonSliceReducerV2 from '@/store/slices/dataComparisonSliceV2'
import themeSlice from './slices/themeSlice'

import diagnosticMapViewSliceReducer from '@/store/slices/DiagnosticMapView/diagnosticMapViewSlice'
import diagnosticSliceReducer from '@/store/slices/Diagnostic/diagnosticSlice'
import flowLogSliceReducer from '@/store/slices/FlowLogSlice'
import interfaceCommonDataSliceReducer from '@/store/slices/interfaceCommonDataSlice'
import userSliceReducer from '@/store/slices/user/userSlice'
import interfaceSliceReducer from '@/store/slices/interface/interfaceSlice'
import interfaceByOrganizationSliceReducer from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import weatherDashboardSliceReducerV2 from '@/store/slices/WeatherDashboardV2/weatherDashboardSlice'
import weatherDashboardSliceV2 from '@/store/slices/WeatherDashboardV2/weatherDashboardSlice'

import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import stationGeoTagDeviceSliceReducer from '@store/slices/StataionGeoTagDevice/stationGeoTagDeviceSlice'
import stationOverviewSliceReducer from '@store/slices/StationOverview/stationOverviewSlice'
import globalExclusionTimeSliceReducer from '@store/slices/globalExclusionTimeSlice'
import mapViewSlice from './slices/MapView/mapViewSlice'

const EXPIRE_TIME = 20 * 60 * 1000

const expirationTransform = createTransform(
  (inboundState) => ({
    data: inboundState,
    timestamp: Date.now(),
  }),
  (outboundState: any) => {
    if (!outboundState) return undefined

    const { data, timestamp } = outboundState
    const isExpired = Date.now() - timestamp > EXPIRE_TIME

    return isExpired ? undefined : data
  },
  { whitelist: ['diagnosticMapView'] },
)

const persistConfig = {
  key: 'diagnosticMapView',
  storage,
  transforms: [expirationTransform],
  // whitelist: ['stations'],
  blacklist: [
    'loading',
    'isLoadingStationError',
    'isLoadingLeaks',
    'isLoadingLowVoltage',
    'error',
    'lastRunningMode',
  ],
  debug: import.meta.env.ENV_NAME !== 'production',
}

export const persistedDiagnosticMapViewReducer = persistReducer(
  persistConfig,
  diagnosticMapViewSliceReducer,
)

export const persistedWeatherDashboardReducer = persistReducer(
  persistConfig,
  weatherDashboardSliceReducer,
)

export const persistedSrcInterfaceReducer = persistReducer(
  persistConfig,
  srcInterfaceSliceReducer,
)

const store = configureStore({
  reducer: {
    counter: counterSlice,
    layout: layoutSlice,
    theme: themeSlice,
    user: userSliceReducer,
    interface: interfaceSliceReducer,
    interfaceByOrganization: interfaceByOrganizationSliceReducer,
    // diagnosticMapView: persistedDiagnosticMapViewReducer,
    diagnosticMapView: diagnosticMapViewSliceReducer,
    diagnostic: diagnosticSliceReducer,
    weatherDashboardSliceV2: weatherDashboardSliceV2,
    weatherDashboard: weatherDashboardSliceReducer,
    soilMoistureDashboard: soilMoistureDashboardSliceReducer,
    srcInterface: srcInterfaceSliceReducer,
    // sb3ClimateControl: sb3ClimateControlSlice,
    dataComparison: dataComparisonSliceReducer,
    dataComparisonV2: dataComparisonSliceReducerV2,
    // sb3DataComparison: sb3DataComparisonSlice,
    // sb3AlertNotification: sb3AlertNotification,
    interfaceCommonData: interfaceCommonDataSliceReducer,
    flowLog: flowLogSliceReducer,
    stationGeoTagDevice: stationGeoTagDeviceSliceReducer,
    stationOverview: stationOverviewSliceReducer,
    globalExclusionTime: globalExclusionTimeSliceReducer,
    weatherDashboardV2: weatherDashboardSliceReducerV2,
    mapViewSlice: mapViewSlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
})

export const persistor = persistStore(store)

export default store

export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
